// Keep field paths and JSON types intact when inspecting or comparing payloads.
export const fieldType = (value) => {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value;
};

export const isEmptyValue = (value) => value == null || value === '' ||
  (typeof value === 'object' && Object.keys(value).length === 0);

export const exactValue = (value) => value === undefined ? 'undefined' : JSON.stringify(value);

export const isImageRecord = (record) => record && typeof record === 'object' &&
  ['type', 'recordType', 'record_type', 'RecordType', 'mimeType', 'mime_type'].some((key) =>
    /^(image|image\/[a-z0-9.+-]+)$/i.test(String(record[key] || '').trim()));

export const telemetryImageSource = (path, value, imageRecord = false) => {
  if (typeof value !== 'string' || !value.trim()) return '';
  const source = value.trim();
  // Never turn non-web schemes or embedded markup into preview requests.
  if (/^[a-z][a-z0-9+.-]*:/i.test(source) && !/^https?:\/\//i.test(source)) return '';
  if (/[<>\r\n]/.test(source) || source.startsWith('//')) return '';
  const key = path.slice(path.lastIndexOf('.') + 1).replace(/_/g, '').toLowerCase();
  const namedImage = /^(image|latestimage|screenshot|thumbnail)(url|uri|path|name)?$/.test(key);
  const typedImage = imageRecord && /^(url|uri|src|path|filename|fileurl)$/.test(key);
  const imageFile = /\.(png|jpe?g|gif|webp|avif|bmp|svg)(?:[?#].*)?$/i.test(source);
  return namedImage || typedImage || imageFile ? source : '';
};

export const flattenTelemetry = (value, path = 'record', group = 'root', imageRecord = false) => {
  if (value === null || typeof value !== 'object' || Object.keys(value).length === 0) {
    const imageSource = telemetryImageSource(path, value, imageRecord);
    return [{ path, group, value, type: fieldType(value), ...(imageSource ? { imageSource } : {}) }];
  }
  return Object.entries(value).flatMap(([key, child]) => flattenTelemetry(
    child,
    Array.isArray(value) ? `${path}[${key}]` : `${path}.${key}`,
    path === 'record' ? key : group,
    !!isImageRecord(value)
  ));
};

export const compareTelemetry = (payload, baseline) => {
  const current = flattenTelemetry(payload);
  if (baseline === undefined) return current;
  const previous = new Map(flattenTelemetry(baseline).map((field) => [field.path, field]));
  const paths = new Set(current.map((field) => field.path));
  return [
    ...current.map((field) => {
      const before = previous.get(field.path);
      return {
        ...field,
        previousValue: before?.value,
        previousImageSource: before?.imageSource,
        change: !before ? 'added' : exactValue(field.value) === exactValue(before.value) ? 'unchanged' : 'changed',
      };
    }),
    ...[...previous.values()].filter((field) => !paths.has(field.path)).map((field) => ({
      ...field, previousValue: field.value, previousImageSource: field.imageSource, imageSource: undefined, value: undefined, change: 'removed',
    })),
  ];
};

export const filterTelemetry = (fields, { query = '', group = '', type = '', hideEmpty = false, changedOnly = false } = {}) => {
  const needle = query.trim().toLowerCase();
  return fields.filter((field) =>
    (!group || field.group === group) &&
    (!type || field.type === type) &&
    (!hideEmpty || !isEmptyValue(field.change === 'removed' ? field.previousValue : field.value)) &&
    (!changedOnly || (field.change && field.change !== 'unchanged')) &&
    (!needle || `${field.path} ${exactValue(field.value)} ${field.change ? exactValue(field.previousValue) : ''}`.toLowerCase().includes(needle))
  );
};

export const telemetryCsv = (fields) => {
  const quote = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  return [
    ['field_path', 'type', 'value_json', 'change', 'previous_value_json'],
    ...fields.map((field) => [field.path, field.type,
      field.change === 'removed' ? '' : exactValue(field.value), field.change || '',
      field.change && field.change !== 'added' ? exactValue(field.previousValue) : '']),
  ].map((row) => row.map(quote).join(',')).join('\r\n');
};

export const sectionLabel = (value) => value === 'root' ? 'Root value' :
  value.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]/g, ' ').replace(/^./, (c) => c.toUpperCase());

export const parseTimestamp = (value) => {
  if (value == null || value === '') return 0;
  const timestamp = Number(value);
  if (Number.isFinite(timestamp)) return timestamp;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const formatCaptureTime = (value) => {
  const timestamp = parseTimestamp(value);
  if (!timestamp || Number.isNaN(new Date(timestamp).getTime())) return 'Unknown capture time';
  return new Date(timestamp).toLocaleString('en-GB');
};

export const normalizeTelemetryRecords = (response) => {
  const parse = (value) => {
    if (typeof value !== 'string') return value;
    try { return JSON.parse(value); } catch { return value; }
  };
  const collection = [response, response?.data, response?.data?.data, response?.records,
    response?.items, response?.results].find(Array.isArray) ||
    (response && typeof response === 'object' ? [response] : []);
  return collection.map((entry, index) => {
    const candidate = parse(entry?.payload) ?? parse(entry?.data) ?? parse(entry?.telemetry) ??
      parse(entry?.jsonBody) ?? parse(entry?.json) ?? entry;
    const payload = candidate && typeof candidate === 'object' ? candidate : { value: candidate };
    const metadata = { ...entry };
    ['data', 'payload', 'telemetry', 'jsonBody', 'json'].forEach((key) => delete metadata[key]);
    const timestamp = parseTimestamp(payload.recordDate ?? metadata.recordDate ?? payload.timestamp ??
      payload.capturedAt ?? payload.captured_at ?? metadata.timestamp ?? metadata.createdAt ?? metadata.created_at);
    return { id: `${entry?.id ?? 'telemetry'}-${index}`, entry: metadata, payload, timestamp };
  }).sort((a, b) => b.timestamp - a.timestamp);
};
